import {
    AgentLinkRequestDTO,
    AgentResponseDTO,
    InstitutionsLinkAgentQueryDTO,
    InstitutionsQueryDTO,
    InstitutionsRequestDTO,
    InstitutionsResponseDTO,
    InstitutionUserPerfis,
    UsersQueryDTO,
    UsersRequestDTO
} from 'models'
import { createContext, useContext, useEffect, useState } from 'react'
import InstitutionsService from 'services/institutions'

import { InstitutionsContextData, InstitutionsContextProps } from './interface'

export const InstitutionsContext = createContext({} as InstitutionsContextData)

export const useInstitutionsContext = () => {
    return useContext(InstitutionsContext)
}

export const InstitutionsProvider = ({
    children
}: InstitutionsContextProps) => {
    const [currentInstitution, setCurrentInstitution] = useState<
        InstitutionsResponseDTO | undefined
    >()
    const [currentAgent, setCurrentAgent] = useState<
        AgentResponseDTO | undefined
    >()
    const [institutions, setInstitutions] = useState<InstitutionsResponseDTO[]>(
        []
    )
    const [agents, setAgents] = useState<AgentResponseDTO[]>([])
    const [userPerfis, setUserPerfis] = useState<InstitutionUserPerfis[]>([])

    const [loadingInstitution, setLoadingInstitution] = useState(true)
    const [loadingCreateUser, setLoadingCreateUser] = useState(false)

    const setInstitution = (institution: InstitutionsResponseDTO | undefined) =>
        setCurrentInstitution(institution)

    const getInstitutions = (query?: InstitutionsQueryDTO) => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitutions(query)
            .then(response => {
                setInstitutions(response.data)
                return response
            })
            .finally(() => setLoadingInstitution(false))
    }

    const getInstitution = (id: string) => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitution(id).finally(() =>
            setLoadingInstitution(false)
        )
    }

    const createInstitutions = (data: InstitutionsRequestDTO) => {
        setLoadingInstitution(true)
        return InstitutionsService.createInstitutions(data).finally(() =>
            setLoadingInstitution(false)
        )
    }

    const createInstitutionUser = (id: string, data: UsersRequestDTO) => {
        setLoadingCreateUser(true)
        return InstitutionsService.createInstitutionUser(id, data).finally(() =>
            setLoadingCreateUser(false)
        )
    }

    const editInstitutionUser = (
        id: string,
        idUser: string,
        data: UsersRequestDTO
    ) => {
        setLoadingInstitution(true)
        return InstitutionsService.editInstitutionUser(
            id,
            idUser,
            data
        ).finally(() => setLoadingInstitution(false))
    }
    const getInstitutionUser = (
        id: string,
        idUser: string,
        isBackoffice: boolean
    ) => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitutionUser(
            id,
            idUser,
            isBackoffice
        ).finally(() => setLoadingInstitution(false))
    }

    const getInstitutionUsers = (id: string, query?: UsersQueryDTO) => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitutionUsers(id, query).finally(() =>
            setLoadingInstitution(false)
        )
    }

    const getInstitutionsPerfis = () => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitutionsPerfis()
            .then(response => {
                const { data } = response
                setUserPerfis(data)
                return response
            })
            .finally(() => setLoadingInstitution(false))
    }

    const getLinkInstitutionAgent = (
        id: string,
        query?: InstitutionsLinkAgentQueryDTO
    ) => {
        setLoadingInstitution(true)
        return InstitutionsService.getLinkInstitutionAgent(id, query).finally(
            () => setLoadingInstitution(false)
        )
    }

    const getInstitutionAgent = (
        id: string,
        query?: InstitutionsLinkAgentQueryDTO
    ) => {
        setLoadingInstitution(true)
        return InstitutionsService.getInstitutionAgent(id, query).finally(() =>
            setLoadingInstitution(false)
        )
    }

    const createLinkInstitutionAgent = (
        id: string,
        data: AgentLinkRequestDTO
    ) => {
        setLoadingInstitution(true)
        return InstitutionsService.createLinkInstitutionAgent(id, data).finally(
            () => setLoadingInstitution(false)
        )
    }

    const deleteLinkInstitutionAgent = async (id: string, idAgent: string) => {
        return InstitutionsService.deleteLinkInstitutionAgent(
            id,
            idAgent
        ).finally(() => setLoadingInstitution(false))
    }

    const value = {
        institutions,
        userPerfis,
        currentInstitution,
        loadingInstitution,
        loadingCreateUser,
        setInstitution,
        setInstitutions,
        setLoadingInstitution,
        getInstitutions,
        getInstitution,
        createInstitutions,
        getInstitutionUser,
        editInstitutionUser,
        getInstitutionUsers,
        createInstitutionUser,
        getInstitutionsPerfis,
        createLinkInstitutionAgent,
        deleteLinkInstitutionAgent,
        getLinkInstitutionAgent,
        setCurrentAgent,
        currentAgent,
        getInstitutionAgent,
        agents,
        setAgents
    }

    useEffect(() => {
        setLoadingInstitution(false)
    }, [])

    return (
        <InstitutionsContext.Provider value={value}>
            {children}
        </InstitutionsContext.Provider>
    )
}
